import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Subscription } from "@/integrations/supabase/types/";
import { Calendar, CreditCard, Mail, Phone, User, DollarSign, Hash } from "lucide-react";
import { format } from "date-fns";

interface PaymentDetailsDialogProps {
  subscription: Subscription | null;
  onClose: () => void;
}

export const PaymentDetailsDialog = ({ subscription, onClose }: PaymentDetailsDialogProps) => {
  if (!subscription) return null;

  const getStatusColor = (status?: string) => {
    switch (status) {
      case "completed":
        return "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300";
      case "pending":
        return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300";
      case "cancelled":
        return "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300";
      default:
        return "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300";
    }
  };

  return (
    <Dialog open={!!subscription} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle className="text-2xl">Payment Details</DialogTitle>
          <DialogDescription>
            View complete payment and subscription information
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold">{subscription.plan_name}</h3>
              <p className="text-sm text-muted-foreground">Payment Information</p>
            </div>
            <Badge className={getStatusColor(subscription.payment_status)}>
              {subscription.payment_status || "Unknown"}
            </Badge>
          </div>

          <Separator />

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1">
              <div className="flex items-center text-sm text-muted-foreground">
                <DollarSign className="h-4 w-4 mr-2" />
                Amount
              </div>
              <p className="text-lg font-semibold">
                {subscription.currency || "USD"} {subscription.price.toLocaleString()}
              </p>
            </div>
            <div className="space-y-1">
              <div className="flex items-center text-sm text-muted-foreground">
                <Calendar className="h-4 w-4 mr-2" />
                Created Date
              </div>
              <p className="text-lg font-semibold">
                {format(new Date(subscription.created_at), "MMM dd, yyyy")}
              </p>
            </div>
          </div>

          {subscription.transaction_id && (
            <div className="space-y-1">
              <div className="flex items-center text-sm text-muted-foreground">
                <Hash className="h-4 w-4 mr-2" />
                Transaction ID
              </div>
              <p className="font-mono text-sm bg-muted p-2 rounded">
                {subscription.transaction_id}
              </p>
            </div>
          )}

          {(subscription.stripe_subscription_id || subscription.stripe_customer_id) && (
            <>
              <Separator />
              <div className="space-y-3">
                <h4 className="font-semibold flex items-center">
                  <CreditCard className="h-4 w-4 mr-2" />
                  Stripe Information
                </h4>
                {subscription.stripe_subscription_id && (
                  <div className="space-y-1">
                    <p className="text-sm text-muted-foreground">Subscription ID</p>
                    <p className="font-mono text-sm bg-muted p-2 rounded">
                      {subscription.stripe_subscription_id}
                    </p>
                  </div>
                )}
                {subscription.stripe_customer_id && (
                  <div className="space-y-1">
                    <p className="text-sm text-muted-foreground">Customer ID</p>
                    <p className="font-mono text-sm bg-muted p-2 rounded">
                      {subscription.stripe_customer_id}
                    </p>
                  </div>
                )}
              </div>
            </>
          )}

          {(subscription.client_name || subscription.client_email || subscription.client_phone) && (
            <>
              <Separator />
              <div className="space-y-3">
                <h4 className="font-semibold">Client Information</h4>
                {subscription.client_name && (
                  <div className="flex items-center">
                    <User className="h-4 w-4 mr-2 text-muted-foreground" />
                    <span>{subscription.client_name}</span>
                  </div>
                )}
                {subscription.client_email && (
                  <div className="flex items-center">
                    <Mail className="h-4 w-4 mr-2 text-muted-foreground" />
                    <span>{subscription.client_email}</span>
                  </div>
                )}
                {subscription.client_phone && (
                  <div className="flex items-center">
                    <Phone className="h-4 w-4 mr-2 text-muted-foreground" />
                    <span>{subscription.client_phone}</span>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
